import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import Button from '../../../../../ui/canon/Button';
import { DownloadIcon, PrinterIcon, ArrowLeftIcon, FileTextIcon, AlertCircleIcon, LoaderIcon } from 'lucide-react';
import DocumentPreview from '../components/DocumentPreview';
import { exportToPDF, exportToMarkdown, isPDFExportAvailable } from '../services/PDFExportService';
import { generatePlanDocument, generateAssessmentReport } from '../services/DocumentGeneratorService';

const GET_PLAN_DETAILS = gql`
  query GetPlanDetails($planTemplateId: ID!) {
    planTemplate(id: $planTemplateId) {
      id
      name
      slug
      description
      version
      planModules {
        id
        title
        description
        planSections {
          id
          title
          description
          planQuestions {
            id
            questionText
            helpText
            widgetType
            placeholder
            options
            required
            purpose
            category
            outputSection
            gapAction
          }
        }
      }
    }
    userPlanProgress(planTemplateId: $planTemplateId) {
      id
      status
      completionPercentage
      userPlanResponses {
        id
        planQuestionId
        answer
        completedAt
        planQuestion {
          id
          questionText
          widgetType
        }
      }
    }
    getCurrentUser {
      id
      name
      allowed_name
      company {
        id
        name
      }
    }
  }
`;

const PlanExport = ({ planTemplateId }) => {
  const intl = useIntl();
  const [pdfAvailable, setPdfAvailable] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  
  const { loading, error, data } = useQuery(GET_PLAN_DETAILS, {
    variables: { planTemplateId }
  });

  // Check if PDF export is available on mount
  useEffect(() => {
    isPDFExportAvailable().then(setPdfAvailable);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-succession-bg-secondary flex items-center justify-center">
        <div className="text-center">
          <LoaderIcon className="w-8 h-8 animate-spin mx-auto mb-2 text-succession-primary" />
          <p className="text-succession-text-secondary">{intl.formatMessage({ id: 'succession.plans.loading_plan' })}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-succession-bg-secondary flex items-center justify-center">
        <div className="text-center text-red-500 max-w-lg px-4">
          <AlertCircleIcon className="w-8 h-8 mx-auto mb-2" />
          <p>{intl.formatMessage({ id: 'succession.plans.error_loading_plan' })}</p>
          <pre className="text-xs text-left bg-red-50 border border-red-200 rounded p-3 overflow-auto max-h-64 text-red-700 whitespace-pre-wrap">
            {error.message}
            {error.graphQLErrors?.map((e, i) => `\n[GraphQL] ${e.message}`).join('')}
            {error.networkError ? `\n[Network] ${error.networkError.message}` : ''}
          </pre>
        </div>
      </div>
    );
  }

  const { planTemplate, userPlanProgress, getCurrentUser } = data;

  if (!planTemplate) {
    return (
      <div className="min-h-screen bg-succession-bg-secondary flex items-center justify-center">
        <div className="text-center text-red-500">
          <AlertCircleIcon className="w-8 h-8 mx-auto mb-2" />
          <p>Plan template not found (ID: {planTemplateId || 'unknown'})</p>
        </div>
      </div>
    );
  }

  const userName = getCurrentUser?.allowed_name || getCurrentUser?.name || 'User';
  const companyName = getCurrentUser?.company?.name || 'Practice';
  
  // Construct progress object for DocumentPreview
  const progress = {
    ...userPlanProgress,
    planTemplate
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    if (!pdfAvailable) {
      setExportError('PDF export is not available. Please install html2pdf.js package.');
      return;
    }
    
    setExporting(true);
    setExportError(null);
    
    try {
      const filename = `${planTemplate.slug || 'succession-plan'}-${userName.replace(/\s+/g, '-').toLowerCase()}`;
      await exportToPDF('plan-document-preview', filename);
    } catch (err) {
      console.error('PDF export failed:', err);
      setExportError('Failed to generate PDF. Please try the print option instead.');
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadMarkdown = () => {
    const markdown = generatePlanDocument(progress, userName, companyName);
    const filename = `${planTemplate.slug || 'succession-plan'}-${userName.replace(/\s+/g, '-').toLowerCase()}`;
    exportToMarkdown(markdown, filename);
  };

  const handleDownloadAssessment = () => {
    const markdown = generateAssessmentReport(progress, userName);
    const filename = `assessment-${userName.replace(/\s+/g, '-').toLowerCase()}`;
    exportToMarkdown(markdown, filename);
  };

  return (
    <div className="min-h-screen bg-succession-bg-secondary">
      {/* Header - hidden when printing */}
      <div className="sticky top-0 z-10 bg-succession-bg-secondary border-b border-succession-border p-4 print:hidden">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Button href={`/succession/plans/${planTemplate?.slug || planTemplateId}`} as="a" variant="ghost" className="pl-0">
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            {intl.formatMessage({ id: 'succession.plans.back_to_editor' })}
          </Button>
          
          <div className="flex gap-3 items-center">
            {exportError && (
              <span className="text-red-500 text-sm mr-2">{exportError}</span>
            )}
            
            {/* Export Options Dropdown */}
            <div className="relative group">
              <Button variant="outline">
                <FileTextIcon className="w-4 h-4 mr-2" />
                {intl.formatMessage({ id: 'succession.plans.export_button' })}
              </Button>
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 hidden group-hover:block">
                <button
                  onClick={handleDownloadMarkdown}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center"
                >
                  <FileTextIcon className="w-4 h-4 mr-2" />
                  {intl.formatMessage({ id: 'succession.plans.download_plan_md_file' })}
                </button>
                <button
                  onClick={handleDownloadAssessment}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center border-t"
                >
                  <FileTextIcon className="w-4 h-4 mr-2" />
                  {intl.formatMessage({ id: 'succession.plans.download_assessment_md_file' })}
                </button>
              </div>
            </div>

            <Button variant="outline" onClick={handlePrint}>
              <PrinterIcon className="w-4 h-4 mr-2" />
              {intl.formatMessage({ id: 'succession.plans.print_button' })}
            </Button>

            <Button
              variant="primary"
              onClick={handleDownloadPDF}
              disabled={exporting || !pdfAvailable}
            >
              {exporting ? (
                <>
                  <LoaderIcon className="w-4 h-4 mr-2 animate-spin" />
                  {intl.formatMessage({ id: 'succession.plans.generating' })}
                </>
              ) : (
                <>
                  <DownloadIcon className="w-4 h-4 mr-2" />
                  {intl.formatMessage({ id: 'succession.plans.download_pdf' })}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Document Preview */}
      <div className="max-w-5xl mx-auto py-8 px-4">
        <div className="shadow-xl rounded-xl overflow-hidden">
          <DocumentPreview 
            progress={progress}
            userName={userName}
            companyName={companyName}
          />
        </div>
      </div>
      
      {/* PDF availability warning */}
      {!pdfAvailable && (
        <div className="max-w-5xl mx-auto px-4 pb-8 print:hidden">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircleIcon className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-yellow-800 font-medium">{intl.formatMessage({ id: 'succession.plans.pdf_not_available_title' })}</p>
              <p className="text-yellow-700 text-sm mt-1">
                {intl.formatMessage({ id: 'succession.plans.pdf_not_available_description' })}
                <code className="ml-2 bg-yellow-100 px-2 py-0.5 rounded">yarn add html2pdf.js</code>
              </p>
              <p className="text-yellow-700 text-sm mt-1">
                {intl.formatMessage({ id: 'succession.plans.pdf_not_available_fallback' })}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanExport;
